// frontend/src/components/chat/MessageAttachment.jsx
// Display attachment within a chat message

import React, { useState, useEffect } from "react";
import {
  getPreviewUrl,
  downloadAttachment,
  getFileIcon,
  formatFileSize,
} from "../../utils/chatAttachmentApi";

const MessageAttachment = ({ attachment, isMine }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);

  // Support both mime_type and file_type (backend uses file_type)
  const mimeType = attachment?.file_type || attachment?.mime_type || "";
  const isImage = mimeType.startsWith("image/");

  // Load image preview
  useEffect(() => {
    if (!isImage || !attachment?.attachment_id) return;

    const loadPreview = async () => {
      setIsLoading(true);
      try {
        const result = await getPreviewUrl(attachment.attachment_id);
        setImageUrl(result.previewUrl);
      } catch (err) {
        console.error("Failed to load image preview:", err);
        setError("Failed to load image");
      } finally {
        setIsLoading(false);
      }
    };

    loadPreview();
  }, [attachment?.attachment_id, isImage]);

  // Handle download
  const handleDownload = async () => {
    if (!attachment?.attachment_id) return;
    setIsDownloading(true);
    try {
      await downloadAttachment(attachment.attachment_id);
    } catch (err) {
      console.error("Failed to download:", err);
      alert("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  // Early return if no valid attachment (after all hooks)
  if (!attachment || !attachment.attachment_id) {
    return null;
  }

  // Render image attachment
  if (isImage) {
    return (
      <>
        <div
          className={`message-attachment image ${isMine ? "mine" : "theirs"}`}
        >
          {isLoading ? (
            <div className="attachment-loading">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="attachment-error">
              <i className="bi bi-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={attachment.file_name || "Image"}
              className="attachment-image"
              onClick={() => setShowFullImage(true)}
            />
          ) : null}

          <div className="attachment-overlay">
            <button
              className="attachment-action-btn"
              onClick={handleDownload}
              disabled={isDownloading}
              title="Download"
            >
              {isDownloading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <i className="bi bi-download"></i>
              )}
            </button>
          </div>
        </div>

        {/* Full image modal */}
        {showFullImage && imageUrl && (
          <div
            className="attachment-fullscreen-overlay"
            onClick={() => setShowFullImage(false)}
          >
            <div className="attachment-fullscreen-content">
              <img src={imageUrl} alt={attachment.file_name || "Image"} />
              <div className="attachment-fullscreen-actions">
                <button
                  className="btn btn-light btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                >
                  <i className="bi bi-download me-1"></i>
                  Download
                </button>
                <button
                  className="btn btn-light btn-sm"
                  onClick={() => setShowFullImage(false)}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Render file attachment
  return (
    <div className={`message-attachment file ${isMine ? "mine" : "theirs"}`}>
      <div className="attachment-file-icon">
        <i className={`bi ${getFileIcon(mimeType)}`}></i>
      </div>
      <div className="attachment-file-info">
        <div className="attachment-file-name" title={attachment.file_name}>
          {attachment.file_name?.length > 20
            ? attachment.file_name.substring(0, 17) + "..."
            : attachment.file_name}
        </div>
        <div className="attachment-file-size">
          {formatFileSize(attachment.file_size)}
        </div>
      </div>
      <button
        className="attachment-download-btn"
        onClick={handleDownload}
        disabled={isDownloading}
        title="Download"
      >
        {isDownloading ? (
          <span className="spinner-border spinner-border-sm" />
        ) : (
          <i className="bi bi-download"></i>
        )}
      </button>
    </div>
  );
};

export default MessageAttachment;
